/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { ROUTES } from '@sportninja/common/constants/app'
import { useCountries } from '@sportninja/common/hooks/useCountries'
import { useRegistrationData } from '@sportninja/common/hooks/useRegistrationData'
import { getSports } from '@sportninja/common/selectors/types'
import { Empty, Tabs, Button } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import RegistrationDetailsBilling from './RegistrationDetailsBilling'
import RegistrationDetailsInfo from './RegistrationDetailsInfo'

// Dynamic form imports
import PlayerInfoSection from '../Schedule/RegistrationDetailsModal/components/PlayerInfoSection'
import PlayerPhotoSection from '../Schedule/RegistrationDetailsModal/components/PlayerPhotoSection'
import TeamInfoSection from '../Schedule/RegistrationDetailsModal/components/TeamInfoSection'
import { RegistrationDetailsModalProvider } from '../Schedule/RegistrationDetailsModal/context'
import RegistrationDetailsDynamicStandalone from './RegistrationDetailsDynamicStandalone'
import useUsersMeRegistration from '@sportninja/common/hooks/useUsersMeRegistration'
import { ArrowLeftOutlined } from '@ant-design/icons'
import Text from 'src/components/ts-components/Text'
import colors from '@sportninja/common/constants/appColors'
import LoadingWrapper from 'src/components/ts-components/LoadingWrapper'

/**
 * 🔄 InfoTabDynamic: Dynamic form wrapper component for registration details
 *
 * This component wraps the dynamic form components in the modal provider context
 * and provides the same functionality as the modal but in a page format.
 */
const InfoTabDynamic = ({
  scheduleId,
  registrationId,
  countries,
  types,
  refetch,
  registrationDetails,
  registrationLoading,
  registrationError,
}) => {
  return (
    <RegistrationDetailsModalProvider
      scheduleId={scheduleId}
      registrationId={registrationId || undefined}
      countries={countries}
      types={types}
    >
      <InfoTabDynamicContent
        scheduleId={scheduleId}
        registrationId={registrationId}
        refetch={refetch}
        registrationDetails={registrationDetails}
        registrationLoading={registrationLoading}
        registrationError={registrationError}
      />
    </RegistrationDetailsModalProvider>
  )
}

/**
 * 🔄 InfoTabDynamicContent: Inner content component that uses the modal context
 *
 * This component renders the same structure as the modal "Details" tab
 * but without the modal wrapper.
 */
const InfoTabDynamicContent = ({
  scheduleId,
  registrationId,
  refetch: refetchParent,
  registrationDetails,
  registrationLoading,
  registrationError,
  refetch,
}) => {
  const isTeamRegistration = useMemo(
    () => registrationDetails?.registration_type === 'Team',
    [registrationDetails]
  )

  const [teamName, setTeamName] = useState('')

  useEffect(() => {
    if (registrationDetails?.team?.name && !teamName) {
      setTeamName(registrationDetails.team.name)
    }
  }, [registrationDetails?.team?.name, teamName])

  return (
    <LoadingWrapper loading={registrationLoading || !registrationDetails}>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          padding: 16px;
        `}
      >
        {isTeamRegistration ? (
          <TeamInfoSection
            teamInfo={registrationDetails?.team}
            registrationId={registrationId || ''}
            disableFields={false}
            updateFormData={(field, value) => {
              console.log('🔄 InfoTabDynamicContent: Update team form data', {
                field,
                value,
              })
            }}
            teamName={teamName}
            setTeamName={setTeamName}
            disableTeamName={true}
          />
        ) : (
          <PlayerInfoSection
            playerId={registrationDetails?.player?.id}
            registrationId={registrationId || undefined}
            scheduleId={scheduleId}
            disableFields={false}
          />
        )}
        {!isTeamRegistration ? (
          <PlayerPhotoSection
            formData={
              registrationDetails?.player?.image
                ? { image: registrationDetails.player.image }
                : {}
            }
            updateFormData={(field, value) => {
              console.log('🔄 InfoTabDynamicContent: Update form data', {
                field,
                value,
              })
            }}
            teamId={registrationDetails?.team?.uid}
            playerId={registrationDetails?.player?.id}
            disableFields={false}
            refetch={refetch}
          />
        ) : null}
        <RegistrationDetailsDynamicStandalone
          scheduleId={scheduleId}
          registrationUid={registrationId || ''}
          viewMode={true}
          refetchParent={() => refetchParent(false)}
          registrationDetails={registrationDetails || null}
          loading={registrationLoading}
          error={registrationError}
          refetch={refetch}
          positions={[]}
        />
      </div>
    </LoadingWrapper>
  )
}

const RegistrationDetails = ({ match }) => {
  const SCHEDULE_ID = match.params.id
  const REGISTRATION_ID = new URLSearchParams(window.location.search).get(
    'registration_id'
  )
  const history = useHistory()
  const location = useLocation()
  const { types, fetchTypes } = useRegistrationData()
  const {
    data: registrationDetails,
    loading: registrationLoading,
    error: registrationError,
    refetch,
  } = useUsersMeRegistration(REGISTRATION_ID || undefined)

  // Local tab state, initialize from URL (/info or /billing) or query (?tab=)
  const initialTab = useMemo(() => {
    const searchParams = new URLSearchParams(location.search)
    const queryTab = (searchParams.get('tab') || '').toLowerCase()
    if (queryTab === 'billing') {
      return 'billing' as const
    }
    if (queryTab === 'info') {
      return 'info' as const
    }

    const path = location.pathname || ''
    if (path.endsWith('/billing')) {
      return 'billing' as const
    }
    if (path.endsWith('/info')) {
      return 'info' as const
    }
    return 'info' as const
  }, [location.pathname, location.search])

  const [activeTab, setActiveTab] = useState<'info' | 'billing'>(initialTab)

  // Version-based form selection: Version 1 = legacy fixed forms, others = dynamic JSON-driven forms
  const registrationVersion = useMemo(() => {
    return registrationDetails?.schedule?.registration_version
  }, [registrationDetails?.schedule?.registration_version])

  const isLegacyVersion = registrationVersion === 1
  const useDynamicForms = !isLegacyVersion

  // No header actions needed - forms handle saves internally
  const actions = []

  // Once we open the schedule details page, we need to refetch the registration details
  useEffect(() => {
    if (REGISTRATION_ID && !registrationDetails) {
      refetch()
    }
  }, [REGISTRATION_ID, refetch, registrationDetails])

  const { countries, refetch: refetchCountries } = useCountries()
  useEffect(() => {
    if (!types || types === null) {
      fetchTypes()
    }
  }, [fetchTypes, types])

  useEffect(() => {
    if (!countries || countries === null || countries?.length === 0) {
      refetchCountries()
    }
  }, [countries, refetchCountries])

  // Tab pages with local keys
  const pages = useMemo(
    () => [
      {
        key: 'info',
        label: 'Registration Info',
        component: useDynamicForms ? (
          <InfoTabDynamic
            scheduleId={SCHEDULE_ID}
            registrationId={REGISTRATION_ID}
            countries={countries}
            types={types}
            refetch={refetch}
            registrationDetails={registrationDetails}
            registrationLoading={registrationLoading}
            registrationError={registrationError}
          />
        ) : (
          <RegistrationDetailsInfo
            registrationDetails={(registrationDetails as any) || null}
            countries={countries}
            refetch={refetch}
          />
        ),
        actions: actions,
      },
      {
        key: 'billing',
        label: 'Billing Schedule',
        component: (
          <RegistrationDetailsBilling
            selectedRegistration={(registrationDetails as any) || null}
            currencies={types?.currency || []}
          />
        ),
        actions: [], // No actions for billing tab
      },
    ],
    [
      SCHEDULE_ID,
      useDynamicForms,
      REGISTRATION_ID,
      countries,
      types,
      refetch,
      registrationDetails,
      registrationLoading,
      registrationError,
      actions,
    ]
  )

  // Build tab items for Ant Design Tabs
  const tabItems = pages.map(({ key, label, component }) => ({
    key,
    label,
    children: component,
  }))

  // Local tab change handler
  const handleTabChange = (key: string) => {
    const nextTab = key as 'info' | 'billing'
    setActiveTab(nextTab)

    // Keep URL in sync for deep-linking and refresh behavior
    const search = location.search || ''
    const base = `${ROUTES.REGISTRATION_DETAILS.ROOT}/${SCHEDULE_ID}`
    const nextPath = `${base}/${nextTab}${search}`
    // Avoid duplicate pushes if already matching
    if (location.pathname + location.search !== nextPath) {
      history.replace(nextPath)
    }
  }

  // If the URL changes externally (e.g., direct link), update the active tab
  useEffect(() => {
    if (initialTab !== activeTab) {
      setActiveTab(initialTab)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTab])

  // Refresh registration details whenever switching tabs to keep data fresh, silently to avoid flicker
  useEffect(() => {
    if (REGISTRATION_ID) {
      // Silent refetch keeps existing content rendered (no loading flash)
      refetch(true)
    }
  }, [activeTab, REGISTRATION_ID, refetch])

  // Add a container style for centering and max width
  const pageContainerStyle = css`
    max-width: min(80vw, 1080px);
    margin: 0 auto;
    margin-top: 40px;
    width: 100%;
    .ant-tabs .ant-tabs-tab.ant-tabs-tab-active {
      background-color: transparent !important;
    }
    .ant-tabs-tab {
      background-color: transparent !important;
    }
  `

  const backButtonStyle = css`
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    font-size: 0.9rem;
  `

  const handleBack = () => {
    history.push(ROUTES.REGISTRATIONS.ROOT)
  }

  if (registrationError) {
    return (
      <div css={pageContainerStyle}>
        <div css={backButtonStyle}>
          <Button
            type='link'
            icon={<ArrowLeftOutlined />}
            size='small'
            onClick={handleBack}
          >
            Back
          </Button>
        </div>
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
          `}
        >
          <Empty
            description={registrationError?.message || ''}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      </div>
    )
  }

  return (
    <div css={pageContainerStyle}>
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 4px;
          cursor: pointer;
          margin-bottom: 8px;
        `}
        onClick={handleBack}
      >
        <ArrowLeftOutlined
          style={{ fontSize: '16px', color: colors.PRIMARY }}
        />
        <Text variant='captionSmall' color={colors.PRIMARY} weight='semiBold'>
          Back
        </Text>
      </div>
      <Text
        variant='displaySmall'
        color={colors.NEUTRAL_0}
        weight='semiBold'
        style={`
          margin-bottom: 16px;
        `}
      >
        {`${registrationDetails?.schedule?.name || ''} - ${
          registrationDetails?.team?.name || ''
        }`}
      </Text>
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
        destroyInactiveTabPane={false}
      />
    </div>
  )
}

const mapStateToProps = (state) => {
  return {
    sports: getSports(state),
  }
}

const mapDispatchToProps = (_) => {
  return {}
}
export default connect(mapStateToProps, mapDispatchToProps)(RegistrationDetails)
